import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { extractErrorMessage } from "@/lib/api";
import { Save, Settings2, UserPlus } from "lucide-react";

export const OperatorGrants = ({ operators, competitions, onChanged }) => {
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const startEdit = (op) => {
    setEditing(op.id);
    setSelected(op.competition_ids || []);
    setError("");
  };

  const toggle = (cid) =>
    setSelected((s) => (s.includes(cid) ? s.filter((x) => x !== cid) : [...s, cid]));

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      await api.put(`/admin/operators/${editing}/competitions`, { competition_ids: selected });
      setEditing(null);
      await onChanged();
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="operator-grants">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="overline">{operators.length} operadora(s) cadastrada(s)</div>
        <Link to="/usuarios" data-testid="op-create-link" className="btn-ghost inline-flex items-center gap-2">
          <UserPlus size={14} /> Criar operadora em Usuários
        </Link>
      </div>
      {error && (
        <div className="text-sm text-[#C9A84C] border border-[#C9A84C]/30 bg-[#C9A84C]/10 px-3 py-2 mb-3">{error}</div>
      )}
      <div className="card overflow-x-auto">
        <table className="table-dense">
          <thead>
            <tr>
              <th>Operadora</th>
              <th>Campeonatos liberados</th>
              <th className="text-right pr-4">Ações</th>
            </tr>
          </thead>
          <tbody>
            {operators.length === 0 && (
              <tr>
                <td colSpan={3} className="text-center py-8 text-[var(--text-secondary)]">
                  Nenhuma operadora ainda. Crie uma em <b>Usuários</b> com o papel "Operadora de Evento".
                </td>
              </tr>
            )}
            {operators.map((op) => (
              <React.Fragment key={op.id}>
                <tr data-testid={`op-row-${op.id}`}>
                  <td>
                    <div className="font-semibold">{op.name}</div>
                    <div className="text-xs text-[var(--text-secondary)]">{op.email}</div>
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-1" data-testid={`op-grants-${op.id}`}>
                      {op.competitions.length === 0 && (
                        <span className="text-xs text-[var(--text-secondary)]">— nenhum —</span>
                      )}
                      {op.competitions.map((c) => (
                        <span key={c.id} className="chip chip-success">{c.nome}</span>
                      ))}
                    </div>
                  </td>
                  <td className="text-right pr-4">
                    <button
                      data-testid={`op-manage-${op.id}`}
                      className="btn-ghost inline-flex items-center gap-2"
                      onClick={() => (editing === op.id ? setEditing(null) : startEdit(op))}
                    >
                      <Settings2 size={14} /> {editing === op.id ? "Fechar" : "Gerenciar acessos"}
                    </button>
                  </td>
                </tr>
                {editing === op.id && (
                  <tr>
                    <td colSpan={3} className="bg-black/40 px-6 py-4">
                      <div className="overline mb-3 text-[#C9A84C]">Marque os campeonatos liberados para {op.name}</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 mb-4" data-testid={`op-grant-list-${op.id}`}>
                        {competitions.map((c) => (
                          <label key={c.id} className="flex items-center gap-2 cursor-pointer hover:bg-white/5 px-2 py-1.5 border border-white/5 text-sm">
                            <input
                              type="checkbox"
                              data-testid={`op-grant-${op.id}-${c.id}`}
                              className="accent-[#C9A84C]"
                              checked={selected.includes(c.id)}
                              onChange={() => toggle(c.id)}
                            />
                            <span className="truncate">{c.nome}</span>
                            <span className="ml-auto text-[10px] text-[var(--text-secondary)] tabular-nums">{c.data}</span>
                          </label>
                        ))}
                      </div>
                      <button data-testid={`op-grant-save-${op.id}`} className="btn-primary inline-flex items-center gap-2" disabled={busy} onClick={save}>
                        <Save size={14} /> {busy ? "Salvando..." : `SALVAR (${selected.length})`}
                      </button>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
